import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FaWeightScale } from 'react-icons/fa6';
// @ts-ignore - InputWithIcon is a JSX component without TypeScript definitions
import InputWithIcon from '@/components/InputWithIcon';
import { loadCurrentState, saveCurrentState } from '../storage';
import { useCheckboxWithTimestamp } from '../hooks/useCheckboxWithTimestamp';
import { useNewPreemieContext } from '../NewPreemieContext';

export const useDepartmentAdmissionStatus = () => {
  const saved = loadCurrentState();
  const admission = saved?.departmentAdmission || {};
  const states = [
    admission.monitoring,
    admission.bloodPressure,
    admission.temperature,
    admission.respiratorAdjustment,
    admission.surfactant,
    admission.ampicillin,
    admission.gentamicin,
    admission.caffeineOnAdmission,
    admission.fentanyl,
    admission.glucoseLevel,
    admission.bloodGas,
    admission.bloodCount,
    admission.bloodCulture,
    admission.bloodType,
    admission.g6pd,
    admission.urineCMV
  ];
  const checked = states.filter(Boolean).length;
  return { checked, total: 16 };
};

const DepartmentAdmission: React.FC = () => {
  const saved = loadCurrentState();
  const { weightAssessment, gestationalAgeWeeks: gestationalAge } = useNewPreemieContext();
  const birthWeight = saved?.departmentAdmission?.birthWeight ?? null;
  const activeWeight = birthWeight !== null && birthWeight > 0 ? birthWeight : weightAssessment;
  const [showTemperatureInfo, setShowTemperatureInfo] = useState(false);
  const [showSurfactantInfo, setShowSurfactantInfo] = useState(false);
  const [showAmpicillinInfo, setShowAmpicillinInfo] = useState(false);
  const [showGentamicinInfo, setShowGentamicinInfo] = useState(false);
  const [showCaffeineOnAdmissionInfo, setShowCaffeineOnAdmissionInfo] = useState(false);
  const [showCaffeineAfter24HoursInfo, setShowCaffeineAfter24HoursInfo] = useState(false);
  const [showFentanylInfo, setShowFentanylInfo] = useState(false);
  // const weightLabel = birthWeight !== null && birthWeight > 0 ? 'לפי משקל לידה' : 'לפי הערכת משקל';
  const [admissionTime, setAdmissionTime] = useState<string>(saved?.departmentAdmission?.admissionTime || '');
  const [localBirthWeight, setLocalBirthWeight] = useState<string>(
    saved?.departmentAdmission?.birthWeight?.toString() || ''
  );
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const handleBirthWeightChange = (value: string) => {
    setLocalBirthWeight(value);
    const parsed = value ? parseInt(value, 10) : null;
    saveCurrentState({
      departmentAdmission: {
        ...saved?.departmentAdmission,
        birthWeight: parsed
      }
    }, 'departmentAdmission');
  };

  const handleAdmissionTimeChange = (value: string) => {
    setAdmissionTime(value);
    saveCurrentState({
      departmentAdmission: {
        ...saved?.departmentAdmission,
        admissionTime: value
      }
    }, 'departmentAdmission');
  };

  const isTemperatureOverdue = () => {
    if (!admissionTime || temperature) return false;
    
    const [hours, minutes] = admissionTime.split(':').map(Number);
    const admissionDate = new Date();
    admissionDate.setHours(hours, minutes, 0, 0);
    
    const fifteenMinutesLater = new Date(admissionDate.getTime() + 15 * 60 * 1000);
    return currentTime > fifteenMinutesLater;
  };

  // Medication dose calculations — all use activeWeight (birthWeight takes priority)
  const surfactantDose = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const dose = (200 * (activeWeight / 1000)) / 80;
      return dose.toFixed(1);
    }
    return null;
  }, [activeWeight]);

  const surfactantFormula = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const weightInKg = (activeWeight / 1000).toFixed(2);
      return `(200 × ${weightInKg}) / 80 = ${surfactantDose}`;
    }
    return '';
  }, [activeWeight, surfactantDose]);

  const ampicillinDose = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const dose = 50 * (activeWeight / 1000);
      return dose.toFixed(1);
    }
    return null;
  }, [activeWeight]);

  const ampicillinDiluted = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const dose = 50 * (activeWeight / 1000) * 1.25;
      return dose.toFixed(2);
    }
    return null;
  }, [activeWeight]);

  const ampicillinFormula = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const weightInKg = (activeWeight / 1000).toFixed(2);
      return {
        base: `50 × ${weightInKg} = ${ampicillinDose}`,
        diluted: `50 × ${weightInKg} × 1.25 = ${ampicillinDiluted}`
      };
    }
    return { base: '', diluted: '' };
  }, [activeWeight, ampicillinDose, ampicillinDiluted]);

  const gentamicinDose = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      let mgPerKg = 4.5;
      if (gestationalAge !== null) {
        if (gestationalAge <= 29) mgPerKg = 5;
        else if (gestationalAge >= 30 && gestationalAge <= 34) mgPerKg = 4.5;
      }
      const dose = mgPerKg * (activeWeight / 1000);
      return dose.toFixed(1);
    }
    return null;
  }, [activeWeight, gestationalAge]);

  const gentamicinDiluted = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      let mgPerKg = 4.5;
      if (gestationalAge !== null) {
        if (gestationalAge <= 29) mgPerKg = 5;
        else if (gestationalAge >= 30 && gestationalAge <= 34) mgPerKg = 4.5;
      }
      const dose = mgPerKg * (activeWeight / 1000) * 1.25;
      return dose.toFixed(2);
    }
    return null;
  }, [activeWeight, gestationalAge]);

  const gentamicinFormula = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const weightInKg = (activeWeight / 1000).toFixed(2);
      let mgPerKg = 4.5;
      let frequency = '';
      let formulaText = '4.5mg/kg';
      if (gestationalAge !== null) {
        if (gestationalAge <= 29) {
          mgPerKg = 5;
          frequency = 'פעם ב-48 שעות';
          formulaText = '5mg/kg (פעם ב-48 שעות)';
        } else if (gestationalAge >= 30 && gestationalAge <= 34) {
          mgPerKg = 4.5;
          frequency = 'פעם ב-36 שעות';
          formulaText = '4.5mg/kg (פעם ב-36 שעות)';
        } else {
          formulaText = '4.5mg/kg';
        }
      }
      return { 
        base: `${mgPerKg} × ${weightInKg} = ${gentamicinDose}`,
        diluted: `${mgPerKg} × ${weightInKg} × 1.25 = ${gentamicinDiluted}`,
        frequency, 
        formulaText 
      };
    }
    return { base: '', diluted: '', frequency: '', formulaText: '' };
  }, [activeWeight, gestationalAge, gentamicinDose, gentamicinDiluted]);

  const caffeineOnAdmissionDose = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const dose = 20 * (activeWeight / 1000);
      return dose.toFixed(1);
    }
    return null;
  }, [activeWeight]);

  const caffeineOnAdmissionDiluted = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const dose = 20 * (activeWeight / 1000) * 1.25;
      return dose.toFixed(2);
    }
    return null;
  }, [activeWeight]);

  const caffeineOnAdmissionFormula = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const weightInKg = (activeWeight / 1000).toFixed(2);
      return {
        base: `20 × ${weightInKg} = ${caffeineOnAdmissionDose}`,
        diluted: `20 × ${weightInKg} × 1.25 = ${caffeineOnAdmissionDiluted}`
      };
    }
    return { base: '', diluted: '' };
  }, [activeWeight, caffeineOnAdmissionDose, caffeineOnAdmissionDiluted]);

  const caffeineAfter24HoursDose = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const dose = 10 * (activeWeight / 1000);
      return dose.toFixed(1);
    }
    return null;
  }, [activeWeight]);

  const caffeineAfter24HoursFormula = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const weightInKg = (activeWeight / 1000).toFixed(2);
      return `10 × ${weightInKg} = ${caffeineAfter24HoursDose}`;
    }
    return '';
  }, [activeWeight, caffeineAfter24HoursDose]);

  const fentanylDose = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const dose = 1 * (activeWeight / 1000);
      return dose.toFixed(2);
    }
    return null;
  }, [activeWeight]);

  const fentanylFormula = useMemo(() => {
    if (activeWeight !== null && activeWeight > 0) {
      const weightInKg = (activeWeight / 1000).toFixed(2);
      return `1mcg/kg × ${weightInKg}kg = ${fentanylDose}`;
    }
    return '';
  }, [activeWeight, fentanylDose]);

  const [monitoring, setMonitoring] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'monitoring',
    saved?.departmentAdmission?.monitoring || false
  );
  const [bloodPressure, setBloodPressure] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'bloodPressure',
    saved?.departmentAdmission?.bloodPressure || false
  );
  const [temperature, setTemperature] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'temperature',
    saved?.departmentAdmission?.temperature || false
  );
  const [respiratorAdjustment, setRespiratorAdjustment] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'respiratorAdjustment',
    saved?.departmentAdmission?.respiratorAdjustment || false
  );
  const [ventilationMethod, setVentilationMethod] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'ventilationMethod',
    saved?.departmentAdmission?.ventilationMethod || false
  );
  const [initialMAP, setInitialMAP] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'initialMAP',
    saved?.departmentAdmission?.initialMAP || false
  );
  const [amp, setAmp] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'amp',
    saved?.departmentAdmission?.amp || false
  );
  const [minimalOxygen, setMinimalOxygen] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'minimalOxygen',
    saved?.departmentAdmission?.minimalOxygen || false
  );
  const [surfactant, setSurfactant] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'surfactant',
    saved?.departmentAdmission?.surfactant || false
  );
  const [ampicillin, setAmpicillin] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'ampicillin',
    saved?.departmentAdmission?.ampicillin || false
  );
  const [gentamicin, setGentamicin] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'gentamicin',
    saved?.departmentAdmission?.gentamicin || false
  );
  const [caffeineOnAdmission, setCaffeineOnAdmission] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'caffeineOnAdmission',
    saved?.departmentAdmission?.caffeineOnAdmission || false
  );
  const [caffeineAfter24Hours, setCaffeineAfter24Hours] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'caffeineAfter24Hours',
    saved?.departmentAdmission?.caffeineAfter24Hours || false
  );
  const [fentanyl, setFentanyl] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'fentanyl',
    saved?.departmentAdmission?.fentanyl || false
  );
  const [glucoseLevel, setGlucoseLevel] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'glucoseLevel',
    saved?.departmentAdmission?.glucoseLevel || false
  );
  const [bloodGas, setBloodGas] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'bloodGas',
    saved?.departmentAdmission?.bloodGas || false
  );
  const [bloodCount, setBloodCount] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'bloodCount',
    saved?.departmentAdmission?.bloodCount || false
  );
  const [bloodCulture, setBloodCulture] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'bloodCulture',
    saved?.departmentAdmission?.bloodCulture || false
  );
  const [bloodType, setBloodType] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'bloodType',
    saved?.departmentAdmission?.bloodType || false
  );
  const [g6pd, setG6pd] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'g6pd',
    saved?.departmentAdmission?.g6pd || false
  );
  const [urineCMV, setUrineCMV] = useCheckboxWithTimestamp(
    'departmentAdmission',
    'urineCMV',
    saved?.departmentAdmission?.urineCMV || false
  );

  return (
    <div className="section-content-wrapper" style={{ padding: '20px', direction: 'rtl' }}>
      <div style={{ marginBottom: '25px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '1rem' }}>
          משקל לידה (גרם):
        </label>
        <div style={{ width: '150px' }}>
          {/* @ts-ignore - InputWithIcon is a JSX component without TypeScript definitions */}
          <InputWithIcon
            icon={<FaWeightScale />}
            type="number"
            inputMode="numeric"
            value={localBirthWeight}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleBirthWeightChange(e.target.value)}
            placeholder="משקל לידה"
            style={{
              backgroundColor: 'white',
              direction: 'rtl'
            }}
          />
        </div>
      </div>

      <div style={{ marginBottom: '25px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '1rem' }}>
          שעת קבלה למחלקה:
        </label>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="time"
            value={admissionTime}
            onChange={(e) => handleAdmissionTimeChange(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #8596AF',
              borderRadius: '8px',
              fontSize: '1rem',
              width: '150px',
              direction: 'rtl',
              backgroundColor: 'white'
            }}
          />
          {admissionTime && (
            <button
              onClick={() => handleAdmissionTimeChange('')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
            >
              נקה
            </button>
          )}
        </div>
      </div>

      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={monitoring}
            onChange={(e) => setMonitoring(e.target.checked)}
          />
          <span>ניטור</span>
        </label>
      </div>

      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={bloodPressure}
            onChange={(e) => setBloodPressure(e.target.checked)}
          />
          <span>מדידת לחץ דם</span>
        </label>
      </div>

      <div className="checkbox-group" style={{ position: 'relative' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={temperature}
            onChange={(e) => setTemperature(e.target.checked)}
          />
          <span style={{
            color: isTemperatureOverdue() ? 'red' : 'inherit',
            fontWeight: isTemperatureOverdue() ? 'bold' : 'normal'
          }}>
            מדידת חום תוך רבע שעה מקבלה
            <FontAwesomeIcon
              icon={faCircleInfo}
              onClick={() => setShowTemperatureInfo(!showTemperatureInfo)}
              style={{
                marginRight: '8px',
                cursor: 'pointer',
                color: '#103C6E',
                fontSize: '14px'
              }}
            />
            {showTemperatureInfo && (
              <>
                <div
                  onClick={() => setShowTemperatureInfo(false)}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: '0',
                    marginTop: '8px',
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '250px',
                    direction: 'rtl',
                    whiteSpace: 'pre-line'
                  }}
                >
                  יעד <span dir="ltr">36.0-37.5</span> (אופטימאלי מעל <span dir="ltr">36.5</span>)
                </div>
              </>
            )}
          </span>
        </label>
      </div>

      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={respiratorAdjustment}
            onChange={(e) => setRespiratorAdjustment(e.target.checked)}
          />
          <span>כיוון מנשם</span>
        </label>
        <ul style={{ margin: '6px 0 0 0', paddingRight: '36px', listStyle: 'disc', fontSize: '0.95rem' }}>
          <li>שיטת הנשמה מועדפת (ב<span dir="ltr">RDS</span> נבחר <span dir="ltr">HFOV</span>)</li>
          <li><span dir="ltr">MAP</span> התחלתי (בהתאם ללחצי ההנשמה שנדרשו לצורך חמצון הפג בחדר הלידה)</li>
          <li><span dir="ltr">AMP</span> (כיוון לפי רטט במגע, תדר התחלתי <span dir="ltr">15</span> הרץ)</li>
          <li>ריכוז חמצן מינימלי לפי יעד סטורציה (בפגים עם <span dir="ltr">RDS 90-93%</span> בקבלה)</li>
        </ul>
      </div>

      <div style={{ marginTop: '25px', marginBottom: '15px' }}>
        <h5 style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>תרופות</h5>
      </div>

      <div className="checkbox-group" style={{ marginRight: '20px', position: 'relative' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={surfactant}
            onChange={(e) => setSurfactant(e.target.checked)}
          />
          <span>
            סורפקטנט - מינון{' '}
            {surfactantDose !== null ? (
              <>
                <strong>{surfactantDose}</strong> מ״ל
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  onClick={() => setShowSurfactantInfo(!showSurfactantInfo)}
                  style={{
                    marginRight: '8px',
                    cursor: 'pointer',
                    color: '#103C6E',
                    fontSize: '14px'
                  }}
                />
                {showSurfactantInfo && (
                  <>
                    <div
                      onClick={() => setShowSurfactantInfo(false)}
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        marginTop: '8px',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        padding: '12px 16px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '250px',
                        direction: 'rtl'
                      }}
                    >
                      <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>נוסחה:</div>
                      <div style={{ marginBottom: '8px', direction: 'ltr', textAlign: 'right' }}>(200mg/kg) / (80mg/ml)</div>
                      <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>חישוב:</div>
                      <div style={{ direction: 'ltr', textAlign: 'right' }}>{surfactantFormula} ml</div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <em>לא הוזן משקל</em>
            )}
          </span>
        </label>
      </div>

      <div className="checkbox-group" style={{ marginRight: '20px', position: 'relative' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={ampicillin}
            onChange={(e) => setAmpicillin(e.target.checked)}
          />
          <span>
            {ampicillinDose !== null ? (
              <>
                <div>
                  אמפיצילין - מינון <strong>{ampicillinDose}</strong> מ״ג
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    onClick={() => setShowAmpicillinInfo(!showAmpicillinInfo)}
                    style={{
                      marginRight: '8px',
                      cursor: 'pointer',
                      color: '#103C6E',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ fontSize: '0.9em', marginTop: '2px' }}>להכנה בתוך 5 מ״ל של סליין - <strong>{ampicillinDiluted}</strong> מ״ג</div>
                {showAmpicillinInfo && (
                  <>
                    <div
                      onClick={() => setShowAmpicillinInfo(false)}
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        marginTop: '8px',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        padding: '12px 16px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '280px',
                        direction: 'rtl'
                      }}
                    >
                      <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>נוסחה:</div>
                      <div style={{ marginBottom: '8px', direction: 'ltr', textAlign: 'right' }}>50mg/kg (פעמיים ביום)</div>
                      <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>חישוב:</div>
                      <div style={{ marginBottom: '4px', direction: 'ltr', textAlign: 'right' }}>{ampicillinFormula.base} mg</div>
                      <div style={{ marginBottom: '8px', direction: 'ltr', textAlign: 'right' }}>{ampicillinFormula.diluted} mg (להכנה בתוך 5 מ״ל)</div>
                      <div style={{ marginTop: '8px', borderTop: '1px solid #eee', paddingTop: '8px', fontStyle: 'italic' }}>מוהלים בתוך 5 מ״ל של סליין ונותנים 4 מ״ל ללא שטיפה</div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <em>לא הוזן משקל</em>
            )}
          </span>
        </label>
      </div>

      <div className="checkbox-group" style={{ marginRight: '20px', position: 'relative' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={gentamicin}
            onChange={(e) => setGentamicin(e.target.checked)}
          />
          <span>
            {gentamicinDose !== null ? (
              <>
                <div>
                  גארמיצין - מינון <strong>{gentamicinDose}</strong> מ״ג
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    onClick={() => setShowGentamicinInfo(!showGentamicinInfo)}
                    style={{
                      marginRight: '8px',
                      cursor: 'pointer',
                      color: '#103C6E',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ fontSize: '0.9em', marginTop: '2px' }}>להכנה בתוך 5 מ״ל של סליין - <strong>{gentamicinDiluted}</strong> מ״ג</div>
                {showGentamicinInfo && (
                  <>
                    <div
                      onClick={() => setShowGentamicinInfo(false)}
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        marginTop: '8px',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        padding: '12px 16px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '280px',
                        direction: 'rtl'
                      }}
                    >
                      <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>נוסחה:</div>
                      <div style={{ marginBottom: '8px', direction: 'ltr', textAlign: 'right' }}>{gentamicinFormula.formulaText}</div>
                      <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>חישוב:</div>
                      <div style={{ marginBottom: '4px', direction: 'ltr', textAlign: 'right' }}>{gentamicinFormula.base} mg</div>
                      <div style={{ marginBottom: '8px', direction: 'ltr', textAlign: 'right' }}>{gentamicinFormula.diluted} mg (להכנה בתוך 5 מ״ל)</div>
                      {gentamicinFormula.frequency && (
                        <div style={{ marginTop: '4px', fontStyle: 'italic' }}>{gentamicinFormula.frequency}</div>
                      )}
                      <div style={{ marginTop: '8px', borderTop: '1px solid #eee', paddingTop: '8px', fontStyle: 'italic' }}>מוהלים בתוך 5 מ״ל של סליין ונותנים 4 מ״ל ללא שטיפה</div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <em>לא הוזן משקל</em>
            )}
          </span>
        </label>
      </div>

      <div className="checkbox-group" style={{ marginRight: '20px', position: 'relative' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={fentanyl}
            onChange={(e) => setFentanyl(e.target.checked)}
          />
          <span>
            פנטניל -{' '}
            {fentanylDose !== null ? (
              <>
                להכין <strong>{fentanylDose}</strong> מיקרוגרם מהולים למתן בפוש איטי
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  onClick={() => setShowFentanylInfo(!showFentanylInfo)}
                  style={{
                    marginRight: '8px',
                    cursor: 'pointer',
                    color: '#103C6E',
                    fontSize: '14px'
                  }}
                />
                {showFentanylInfo && (
                  <>
                    <div
                      onClick={() => setShowFentanylInfo(false)}
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        marginTop: '8px',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        padding: '12px 16px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '340px',
                        direction: 'rtl'
                      }}
                    >
                      <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>נוסחה:</div>
                      <div style={{ marginBottom: '8px', direction: 'ltr', textAlign: 'right' }}>1mcg/kg</div>
                      <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>חישוב:</div>
                      <div style={{ marginBottom: '8px', direction: 'ltr', textAlign: 'right' }}>{fentanylFormula} mcg</div>
                      <div style={{ marginTop: '8px', borderTop: '1px solid #eee', paddingTop: '8px' }}>
                        <div style={{ marginBottom: '6px' }}>יש לשאוב 0.1 מ״ל של התרופה (שהם 5 מיקרוגרם), ולהשלים עד 1 מ״ל עם <span dir="ltr">NACL 0.9%</span></div>
                        <div style={{ marginBottom: '6px' }}>לאחר המיהול יהיו במזרק 5 מיקרוגרם ב-1 מ״ל</div>
                        <div>ולתת לפי המינון הנדרש למשקל (<strong>{fentanylDose}</strong> מיקרוגרם)</div>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <em>לא הוזן משקל</em>
            )}
          </span>
        </label>
      </div>

      <div className="checkbox-group" style={{ marginRight: '20px', position: 'relative' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={caffeineOnAdmission}
            onChange={(e) => setCaffeineOnAdmission(e.target.checked)}
          />
          <span>
            {caffeineOnAdmissionDose !== null ? (
              <>
                <div>
                  קפאין בקבלה - מינון <strong>{caffeineOnAdmissionDose}</strong> מ״ג
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    onClick={() => setShowCaffeineOnAdmissionInfo(!showCaffeineOnAdmissionInfo)}
                    style={{
                      marginRight: '8px',
                      cursor: 'pointer',
                      color: '#103C6E',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ fontSize: '0.9em', marginTop: '2px' }}>להכנה בתוך 5 מ״ל של סליין - <strong>{caffeineOnAdmissionDiluted}</strong> מ״ג</div>
                {showCaffeineOnAdmissionInfo && (
                  <>
                    <div
                      onClick={() => setShowCaffeineOnAdmissionInfo(false)}
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        marginTop: '8px',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        padding: '12px 16px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '280px',
                        direction: 'rtl'
                      }}
                    >
                      <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>נוסחה:</div>
                      <div style={{ marginBottom: '8px', direction: 'ltr', textAlign: 'right' }}>20mg/kg</div>
                      <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>חישוב:</div>
                      <div style={{ marginBottom: '4px', direction: 'ltr', textAlign: 'right' }}>{caffeineOnAdmissionFormula.base} mg</div>
                      <div style={{ marginBottom: '8px', direction: 'ltr', textAlign: 'right' }}>{caffeineOnAdmissionFormula.diluted} mg (להכנה בתוך 5 מ״ל)</div>
                      <div style={{ marginTop: '8px', borderTop: '1px solid #eee', paddingTop: '8px', fontStyle: 'italic' }}>מוהלים בתוך 5 מ״ל של סליין ונותנים 4 מ״ל ללא שטיפה</div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <em>לא הוזן משקל</em>
            )}
          </span>
        </label>
      </div>

      <div style={{ marginRight: '20px', paddingRight: '30px', padding: '6px 0', fontSize: '0.95rem', position: 'relative' }}>
        <span style={{ fontWeight: '500' }}>לא לשכוח לתת קפאין אחרי 24 שעות - מינון{' '}</span>
        {caffeineAfter24HoursDose !== null ? (
          <>
            <strong>{caffeineAfter24HoursDose}</strong> <span dir="ltr">מ"ג</span>
            <FontAwesomeIcon
              icon={faCircleInfo}
              onClick={() => setShowCaffeineAfter24HoursInfo(!showCaffeineAfter24HoursInfo)}
              style={{
                marginRight: '8px',
                cursor: 'pointer',
                color: '#103C6E',
                fontSize: '14px'
              }}
            />
            {showCaffeineAfter24HoursInfo && (
              <>
                <div
                  onClick={() => setShowCaffeineAfter24HoursInfo(false)}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: '0',
                    marginTop: '8px',
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '250px',
                    direction: 'rtl'
                  }}
                >
                  <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>נוסחה:</div>
                  <div style={{ marginBottom: '8px', direction: 'ltr', textAlign: 'right' }}>10mg/kg</div>
                  <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>חישוב:</div>
                  <div style={{ direction: 'ltr', textAlign: 'right' }}>{caffeineAfter24HoursFormula} mg</div>
                </div>
              </>
            )}
          </>
        ) : (
          <em>לא הוזן משקל</em>
        )}
      </div>

      <div style={{ marginTop: '25px', marginBottom: '15px' }}>
        <h5 style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>בדיקות מעבדה</h5>
      </div>

      <div className="checkbox-group" style={{ marginRight: '20px' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={glucoseLevel}
            onChange={(e) => setGlucoseLevel(e.target.checked)}
          />
          <span>רמת גלוקוז</span>
        </label>
      </div>

      <div className="checkbox-group" style={{ marginRight: '20px' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={bloodGas}
            onChange={(e) => setBloodGas(e.target.checked)}
          />
          <span>גזים בדם</span>
        </label>
      </div>

      <div className="checkbox-group" style={{ marginRight: '20px' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={bloodCount}
            onChange={(e) => setBloodCount(e.target.checked)}
          />
          <span>ספירת דם</span>
        </label>
      </div>

      <div className="checkbox-group" style={{ marginRight: '20px' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={bloodCulture}
            onChange={(e) => setBloodCulture(e.target.checked)}
          />
          <span>תרבית דם</span>
        </label>
      </div>

      <div className="checkbox-group" style={{ marginRight: '20px' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={bloodType}
            onChange={(e) => setBloodType(e.target.checked)}
          />
          <span>סוג דם (דם טבורי)</span>
        </label>
      </div>

      <div className="checkbox-group" style={{ marginRight: '20px' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={g6pd}
            onChange={(e) => setG6pd(e.target.checked)}
          />
          <span>G6PD (דם טבורי)</span>
        </label>
      </div>

      <div className="checkbox-group" style={{ marginRight: '20px' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={urineCMV}
            onChange={(e) => setUrineCMV(e.target.checked)}
          />
          <span>שתן ל-CMV</span>
        </label>
      </div>
    </div>
  );
};

export default DepartmentAdmission;
